<script setup lang="ts">
import { computed } from 'vue'
import { useModalStore } from '@/stores/ModalStore'
import Section from './Section.vue'
import { SectionSettings, Alignment, StatContent } from './types'
import { getColorById } from './utils'
import { StatResult } from '@components/customer_page/AssetSelectorModal/types'

interface Props {
  content?: StatContent[]
  editable?: boolean
  settings: SectionSettings
  sectionId: string
}

const props = withDefaults(defineProps<Props>(), {
  settings: () => ({
    alignment: 'center',
    design_settings: {
      colors: [],
      fonts: [],
    },
  }),
  content: () => [],
  editable: false,
})

const emit = defineEmits<{
  (e: 'contentSelected', sectionId: string, assets: StatResult[]): void
}>()

const actualSettings = computed(() => {
  return {
    alignment: 'center' as Alignment,
    title: 'Industry leader in ROI',
    show_title: true,
    ...props.settings,
  }
})

const backgroundColor = computed(() =>
  getColorById(props.settings.design_settings?.colors, 'background_color'),
)

const textColor = computed(() => getColorById(props.settings.design_settings?.colors, 'text_color'))

const modalStore = useModalStore()
const openAssetPickerModal = (): void => {
  modalStore.open('asset_selector_modal', {
    assets: props.content,
    maxNumberOfAssets: 3,
    onAssetsSelected: handleAssetsSelected,
    title: 'Select Featured Statistics',
    type: 'StatAsset',
  })
}

const handleAssetsSelected = (assets: StatResult[]): void => {
  emit('contentSelected', props.sectionId, assets)
}
const statsTextColor = computed(
  () => getColorById(props.settings.design_settings?.colors, 'stats_text_color') || textColor.value,
)
const statsBackgroundColor = computed(() =>
  getColorById(props.settings.design_settings?.colors, 'stats_background_color'),
)
</script>

<template>
  <Section :settings="actualSettings">
    <div class="stats-section">
      <div class="d-flex justify-content-center flex-wrap w-100">
        <UeTypography v-if="content?.length === 0" :contrast-with-color="backgroundColor">
          No stats added yet.
        </UeTypography>
        <div v-for="stat in content" :key="stat.id" class="col-12 col-md-4 mb-3">
          <div
            class="card h-100"
            :style="{ color: statsTextColor, backgroundColor: statsBackgroundColor }"
          >
            <div class="card-body">
              <h2 class="mb-3">{{ stat.headline }}</h2>
              <p class="sentence">{{ stat.sentence }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="editable" class="d-flex justify-content-center">
      <button class="btn fs-sm btn-secondary mt-3" @click="openAssetPickerModal">
        Browse and add stats
      </button>
    </div>
  </Section>
</template>

<style scoped lang="sass">
.stats-section
  margin-top: 2rem
.sentence
  font-weight: 600
</style>
